import { type CreateLabelMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createLabelMutationDocument } from "./label.gql";
import { EVENT_LABEL_CREATED, trackLabelEvent } from "./tracking";

export type CreatedLabel = CreateLabelMutation["createLabel"];

export interface CreateLabelInput {
  workspaceId: string;
  title: string;
  color: string;
  labelGroupId?: string;
}

export interface UseCreateLabelProps {
  onCompleted?: (data: CreatedLabel) => void;
  onError?: (e: Error) => void;
}

export const useCreateLabel = ({ onCompleted, onError }: UseCreateLabelProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: CreateLabelInput) => {
      const client = createOperationClient("createLabel");
      const data = await client.request(createLabelMutationDocument, { input });

      return data.createLabel;
    },
    onSuccess: (label) => {
      // Invalidate and refetch label lists for this workspace
      queryClient.invalidateQueries({
        queryKey: queryKeys.labels.list(label.workspaceId),
      });

      // Invalidate parent label group so its labelList updates
      if (label.labelGroupId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.labelGroups.detail(label.labelGroupId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.labelGroups.list(label.workspaceId),
        });
      }

      // Track the event
      trackLabelEvent(EVENT_LABEL_CREATED, label);

      // Call the onCompleted callback
      if (onCompleted) {
        onCompleted(label);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    createLabel: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
